import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchCurrentUser, loginWithEmailPassword, logoutFromServer, type DbMariaUser } from "../services/dbMariaAuth";

interface ProducerInfo {
  brand: string;
  producerId: string;
}

interface AuthContextType {
  currentUser: DbMariaUser | null;
  login: (email: string, password: string) => Promise<DbMariaUser>;
  logout: () => Promise<void>;
  loading: boolean;
  role?: string;
  producerInfo?: ProducerInfo | null;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  login: async () => { throw new Error("Auth context not initialized."); },
  logout: async () => {},
  loading: true,
  role: undefined,
  producerInfo: null
});

export const useAuth = () => useContext(AuthContext);

function deriveProducerInfo(user: DbMariaUser | null): ProducerInfo | null {
  if (!user) return null;
  const brand = user.brand || user.producerBrand;
  const producerId = user.producerId || user.producer_id;
  if (user.role === "producer" && brand && producerId) {
    return { brand, producerId };
  }
  return null;
}

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<DbMariaUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [producerInfo, setProducerInfo] = useState<ProducerInfo | null>(null);

  useEffect(() => {
    let isMounted = true;

    const hydrateSession = async () => {
      try {
        const user = await fetchCurrentUser();
        if (!isMounted) return;
        setCurrentUser(user);
        if (user) {
          setRole(user.role || undefined);
          setProducerInfo(deriveProducerInfo(user));
        } else {
          setRole(undefined);
          setProducerInfo(null);
        }
      } catch {
        if (isMounted) {
          setCurrentUser(null);
          setRole(undefined);
          setProducerInfo(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    hydrateSession();
    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email: string, password: string) => {
    const user = await loginWithEmailPassword(email, password);
    setCurrentUser(user);
    setRole(user.role || undefined);
    setProducerInfo(deriveProducerInfo(user));
    return user;
  };

  const logout = async () => {
    await logoutFromServer();
    setCurrentUser(null);
    setRole(undefined);
    setProducerInfo(null);
  };

  const value = {
    currentUser,
    login,
    logout,
    loading,
    role,
    producerInfo
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
